import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { loaderStateTrue, loaderStateFalse } from '../../../../Actions/AllAction';
import { connect } from 'react-redux';
import { checkOnlinePaymentStatus } from '../Controller/PaymentController';
import Utility from '../../../../Utility/Utility';

function OnlinePaymentStatus(props) {
    const location = useLocation();
    const history = useHistory();
    const [isProcessing, setIsProcessing] = useState(true);
    const [paymentData, setPaymentData] = useState({});
    const [paymentStatus, setPaymentStatus] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        processPaymentStatus(params);
    }, []);

    const processPaymentStatus = async (params) => {
        const { loaderStateTrue, loaderStateFalse } = props;

        const encryptedData = params.get('encData');

        if (!encryptedData) {
            const lng = localStorage.getItem('i18nextLng') || 'en';
            history.push(`/${lng}/notfound`);
            return;
        }

        loaderStateTrue();
        setIsProcessing(true);

        try {
            const response = await checkOnlinePaymentStatus(encryptedData);
            
            loaderStateFalse();
            setIsProcessing(false);

            if (response && response.success) {
                const data = response.data;
                const verifiedStatus = data?.order_status || data?.payment_status || data?.status;
                
                setPaymentData({
                    orderId: data?.order_id,
                    transactionId: data?.transaction_id,
                    amount: data?.amount,
                    currency: data?.currency || 'INR'
                });
                setPaymentStatus(normalizeStatus(verifiedStatus));
            } else {
                const lng = localStorage.getItem('i18nextLng') || 'en';
                history.push(`/${lng}/notfound`);
            }
        } catch (error) {
            console.error('Payment status verification error:', error);
            loaderStateFalse();
            setIsProcessing(false);
            const lng = localStorage.getItem('i18nextLng') || 'en';
            history.push(`/${lng}/notfound`);
        }
    };

    const normalizeStatus = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'success':
            case 'completed':
            case 'captured':
            case 'authorized':
                return 'success';
            case 'failed':
            case 'failure':
            case 'declined':
                return 'failed';
            case 'timeout':
            case 'expired':
                return 'timeout';
            case 'aborted':
            case 'cancelled':
            case 'canceled':
                return 'aborted';
            case 'invalid':
            case 'error':
            default:
                return 'invalid';
        }
    };

    const renderPaymentStatus = () => {
        const { orderId, transactionId, amount, currency } = paymentData;

        const getStatusConfig = () => {
            switch (paymentStatus) {
                case 'success':
                    return {
                        icon: '✓',
                        title: 'Payment Successful',
                        message: `The payment of ${Utility.getCurrencyValue(amount, currency)} was completed successfully`,
                        backgroundColor: '#28a745',
                        buttonText: 'My Bookings',
                        buttonLink: 'booking-history?payment_status=confirmed'
                    };
                case 'failed':
                    return {
                        icon: '✕',
                        title: 'Payment Failed',
                        message: `The payment of ${Utility.getCurrencyValue(amount, currency)} was unsuccessful or Failed.`,
                        backgroundColor: '#dc3545',
                        buttonText: 'My Bookings',
                        buttonLink: 'booking-history?payment_status=cancelled'
                    };
                case 'timeout':
                    return {
                        icon: '⏱',
                        title: 'Payment Timeout',
                        message: `The payment session has expired. Please try again.`,
                        backgroundColor: '#ffc107',
                        buttonText: 'My Bookings',
                        buttonLink: 'booking-history'
                    };
                case 'aborted':
                    return {
                        icon: '⚠',
                        title: 'Payment Aborted',
                        message: `The payment was cancelled or aborted.`,
                        backgroundColor: '#ff9800',
                        buttonText: 'My Bookings',
                        buttonLink: 'booking-history'
                    };
                case 'invalid':
                default:
                    return {
                        icon: '!',
                        title: 'Invalid Payment',
                        message: `The payment request is invalid or corrupted.`,
                        backgroundColor: '#9e9e9e',
                        buttonText: 'My Bookings',
                        buttonLink: 'booking-history'
                    };
            }
        };

        const config = getStatusConfig();

        const iconStyle = {
            width: '80px',
            height: '80px',
            margin: '0 auto 20px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px',
            color: '#ffffff',
            backgroundColor: config.backgroundColor
        };

        const buttonStyle = {
            width: '100%',
            padding: '12px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: 'pointer',
            backgroundColor: config.backgroundColor,
            color: '#ffffff'
        };

        const infoRowStyle = {
            display: 'flex',
            justifyContent: 'space-between',
            margin: '8px 0',
            fontSize: '14px',
            color: '#555'
        };

        const infoLabelStyle = {
            fontWeight: '600',
            color: '#333'
        };

        const infoValueStyle = {
            fontWeight: '500',
            color: '#000',
            wordBreak: 'break-all'
        };

        return (
            <>
                <div style={iconStyle}>{config.icon}</div>
                <h2>{config.title}</h2>
                <p style={{ color: '#666' }}>{config.message}</p>

                {(orderId || transactionId) && (
                    <div
                        style={{
                            marginTop: '20px',
                            padding: '15px',
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e0e0e0',
                            textAlign: 'left',
                            marginBottom: '10px'
                        }}
                    >
                        {orderId && (
                            <div style={infoRowStyle}>
                                <span style={infoLabelStyle}>Booking ID</span>
                                <span style={infoValueStyle}>{orderId}</span>
                            </div>
                        )}
                        {transactionId && (
                            <div style={infoRowStyle}>
                                <span style={infoLabelStyle}>Transaction ID</span>
                                <span style={infoValueStyle}>{transactionId}</span>
                            </div>
                        )}
                    </div>
                )}

                <button
                    style={buttonStyle}
                    onClick={() => {
                        const lng = localStorage.getItem('i18nextLng') || 'en';
                        window.location.href = `/${lng}/${config.buttonLink}`;
                    }}
                >
                    {config.buttonText}
                </button>
            </>
        );
    };

    const containerStyle = {
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f4f6f8',
        fontFamily: 'Arial, sans-serif'
    };

    const cardStyle = {
        width: '360px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '30px',
        textAlign: 'center',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
    };

    const spinnerStyle = {
        width: '50px',
        height: '50px',
        margin: '0 auto 20px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    };

    return (
        <>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            <div style={containerStyle}>
                <div style={cardStyle}>
                    {isProcessing ? (
                        <>
                            <div style={spinnerStyle}></div>
                            <h3>Processing Payment Status...</h3>
                            <p style={{ color: '#666', marginTop: '10px' }}>
                                Please wait while we verify your payment
                            </p>
                        </>
                    ) : paymentStatus ? (
                        renderPaymentStatus()
                    ) : null}
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (globalState) => {
    return {
        userCredentials: globalState.LoginReducer.userCredentials
    };
};

export default connect(mapStateToProps, { loaderStateTrue, loaderStateFalse })(OnlinePaymentStatus);
